const gridColumns = {
    name: "grid-column",
    path: "/grid-column",
    title: "grid-column",
    description: "Utilities for controlling how elements are sized and placed across grid columns.",
    value: {
        "grid-column": {
            "col-span-<number>": "span <number> / span <number>",
            "col-span-full": "1 / -1",
            "col-auto": "auto",
            "col-<number>": "<number>",
            "-col-<number>": "calc(<number> * -1)",
            "col-[<value>]": "<value>",
        },
        "grid-column-start":{
            "col-start-<number>": "<number>",
            "-col-start-<number>": "calc(<number> * -1)",
            "col-start-auto": "auto",
        },
        "grid-column-end":{
            "col-end-<number>": "<number>",
            "-col-end-<number>": "calc(<number> * -1)",
            "col-end-auto": "auto",
        },
    },
    exampleData: [
        {
            "example": {
                "code": `
<div class="grid grid-columns-3 gap-4">
  <div class="...">01</div>
  <div class="...">02</div>
  <div class="...">03</div>
  <div class="col-span-2 ...">04</div>
  <div class="...">05</div>
  <div class="...">06</div>
  <div class="col-span-2 ...">07</div>
</div>
                `
            },
        },
        {
            "example": {
                "code": `
<div class="grid grid-columns-6 gap-4">
  <div class="col-span-4 col-start-2 ...">01</div>
  <div class="col-start-1 col-end-3 ...">02</div>
  <div class="col-span-2 col-end-7 ...">03</div>
  <div class="col-start-1 col-end-7 ...">04</div>
</div>
                `
            },
        },
        {
            "example": {
                "code": `
<div class="grid grid-columns-6 gap-4">
  <div class="col-[2_/_span_4] ...">01</div>
  <div class="col-[1_/_span_4] ...">02</div>
  <div class="col-span-2 col-end-7 ...">03</div>
  <div class="col-start-1 col-end-7 ...">04</div>
</div>
                `
            },
        },
    ],
    htmlData: [
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
                Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">col-span-<var>&lt;number&gt;</var></code>
                  utilities like col-span-2 and col-span-4 to make an element span n columns:
            </p>

            <div class="relative">
                <div class="absolute inset-0 z-0">
                    <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                </div>
                <div class="grid grid-columns-3 gap-4 font-mono text-sm leading-6 font-bold text-color-white relative">
                    <div class="flex h-14 flex-1 items-center justify-center rounded-lg bg-color-pink-300 p-4">01</div>
                    <div class="flex h-14 flex-1 items-center justify-center rounded-lg bg-color-pink-300 p-4">02</div>
                    <div class="flex h-14 flex-1 items-center justify-center rounded-lg bg-color-pink-300 p-4">03</div>
                    <div class="flex h-14 col-span-2 items-center justify-center rounded-lg bg-color-pink-500 p-4">04</div>
                    <div class="flex h-14 flex-1 items-center justify-center rounded-lg bg-color-pink-300 p-4">05</div>
                    <div class="flex h-14 flex-1 items-center justify-center rounded-lg bg-color-pink-300 p-4">06</div>
                    <div class="flex h-14 col-span-2 items-center justify-center rounded-lg bg-color-pink-500 p-4">07</div>
                </div>
            </div>

            
            `
        },
        {
            data: `
           <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
                Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">col-start-<var>&lt;number&gt;</var>, col-end-<var>&lt;number&gt;</var></code>
                  utilities like col-start-2 and col-end-3 to make an element start or end at the nth grid line:
            </p>

            <div class="relative">

                <div class="grid grid-columns-6 gap-4 font-mono text-sm leading-6 font-bold text-color-white relative">
                    <div class="h-14 rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    <div class="flex h-14 col-span-4 col-start-2 items-center justify-center rounded-lg bg-color-pink-500 p-4">01</div>
                    <div class="h-14 rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    <div class="flex h-14 col-start-1 col-end-3 items-center justify-center rounded-lg bg-color-pink-500 p-4">02</div>
                    <div class="h-14 rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    <div class="h-14 rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    <div class="flex h-14 col-span-2 col-end-7 items-center justify-center rounded-lg bg-color-pink-500 p-4">03</div>
                    <div class="flex h-14 col-start-1 col-end-7 items-center justify-center rounded-lg bg-color-pink-500 p-4">04</div>
                </div>
            </div>
            `
        },
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
                Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">Using a custom value</code>
                  Use utilities like <code class="text-color-gray-800 dark:text-color-white font-semibold">col-[<value>]</code> to set the grid column size and location based on a completely custom value:
            </p>

             <div class="relative">

                <div class="grid grid-columns-6 gap-4 font-mono text-sm leading-6 font-bold text-color-white relative">
                    <div class="h-14 rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    <div class="flex h-14 col-[2_/_span_4] items-center justify-center rounded-lg bg-color-pink-500 p-4">01</div>
                    <div class="h-14 rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    <div class="flex h-14  col-[1_/_span_4] items-center justify-center rounded-lg bg-color-pink-500 p-4">02</div>
                    <div class="h-14 rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    <div class="h-14 rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    <div class="flex h-14 col-start-1 col-end-7 items-center justify-center rounded-lg bg-color-pink-500 p-4">03</div>
                </div>
            </div>
            `
        },
    ]
};

export default gridColumns;

<div className="sr-only col-span-2 grid-columns-3 col-span-3 col-start-2  bg-color-blue-200">

<div class="grid grid-columns-6 gap-4 font-mono text-sm leading-6 font-bold text-color-white relative col-[2_/_span_4] col-[1_/_span_4]">
                    <div class="flex h-14 col-span-4 col-start-2 items-center justify-center rounded-lg bg-color-pink-300 p-4">01</div>
                    <div class="flex h-14 col-start-1 col-end-3 items-center justify-center rounded-lg bg-color-pink-300 p-4">02</div>
                    <div class="flex h-14 col-span-2 col-end-7 items-center justify-center rounded-lg bg-color-pink-300 p-4">03</div>
                    <div class="flex h-14 col-start-1 col-end-7 items-center justify-center rounded-lg bg-color-pink-500 p-4">04</div>
                </div>

</div>